import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MapService } from 'ngx-core-services';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { distinct, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ItemMessageModule } from 'ngx-zcs-components';

import { PositionDetails } from './maps-form.models';

const DEFAULT_ZOOM = 12;
const getLabels = () => ({
  inputPlaceholder: 'Ex. City, Country',
});

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    ItemMessageModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
  selector: 'customer-maps-form',
  templateUrl: './maps-form.component.html',
  styleUrls: ['./maps-form.component.scss'],
})
export class MapsFormComponent implements AfterViewInit, OnChanges {
  @ViewChild('map') mapElement?: ElementRef;

  private _coordinates?: { lat: number; lng: number };
  @Input() set coordinates(value: { lat: number; lng: number } | undefined) {
    this._coordinates = value;
  }
  get coordinates() {
    return this._coordinates;
  }

  get _innerCoordinates() {
    return this.coordinates ? `${this.coordinates.lat.toFixed(6)}, ${this.coordinates.lng.toFixed(6)}` : '???, ???';
  }

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  _editable = true;
  @Input() set editable(value: boolean) {
    this._editable = value;
    (value ? this.enable : this.disable)();
  }
  get editable() {
    return this._editable;
  }

  @Output() position = new EventEmitter<PositionDetails>();

  readonly searchAction$ = new Subject<void>();
  readonly searchInput = new FormGroup({
    address: new FormControl(''),
  });

  get isSearchDisabled() {
    return !this.searchInput.value.address;
  }

  private mapLoaded = false;
  private map?: google.maps.Map<HTMLElement>;
  private marker?: google.maps.Marker;
  private mapClickListener?: google.maps.MapsEventListener;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly mapService: MapService,
  ) {
    this.addMarker = this.addMarker.bind(this);
    this.search = this.search.bind(this);
    this.searchAction$.pipe(
      untilDestroyed(this),
      map(() => this.searchInput.value.address),
      distinct(),
    ).subscribe((address) => {
      if (address) {
        this.search(address);
      }
    });
    this.enable = this.enable.bind(this);
    this.disable = this.disable.bind(this);
  }

  disable() {
    this.mapClickListener?.remove();
    this.map?.setOptions({
      draggable: false,
      fullscreenControl: false,
    });
  }

  enable() {
    this.map?.setOptions({
      draggable: true,
      fullscreenControl: false,
    });

    if (this.mapClickListener) {
      return;
    }
    this.mapClickListener = this.map?.addListener('click', this.addMarker.bind(this));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.mapLoaded) {
      return;
    }

    const coordinatesChange = changes.coordinates;
    if (!coordinatesChange || !coordinatesChange.currentValue) {
      return;
    }

    const { lat: currentLat, lng: currentLng } = coordinatesChange.currentValue;
    const previousLat = coordinatesChange.previousValue?.lat;
    const previousLng = coordinatesChange.previousValue?.lng;

    if (currentLat !== previousLat || currentLng !== previousLng) {
      this.updateMarker();
      (this.editable ? this.enable : this.disable)();
    }
  }

  async ngAfterViewInit() {
    if (this.mapElement) {
      this.map = await this.mapService.getMap(this.mapElement.nativeElement, {});
      this.mapLoaded = true;
      this.updateMarker();
      (this.editable ? this.enable : this.disable)();
    }
  }

  async search(input: string) {
    const results = await this.mapService.codeAddress(input);
    const result = results.pop();
    if (!result) {
      return;
    }
    const latLng = result.geometry.location;
    await this.addMarker({ latLng });
  }

  private async updateMarker() {
    if (this.coordinates) {
      const latLng = await this.mapService.getLatLng(this.coordinates);
      this.addMarker({ latLng });
    }
  }

  async addMarker(event: google.maps.MapMouseEvent | google.maps.IconMouseEvent | { latLng: google.maps.LatLng }) {
    if (!this.map) {
      return;
    }

    const { latLng } = event;
    const currentCoordinates = {
      lat: latLng.lat(),
      lng: latLng.lng(),
    };

    const latLngString = `${currentCoordinates.lat.toFixed(6)}, ${currentCoordinates.lng.toFixed(6)}`;
    if (this._innerCoordinates !== latLngString) {
      this.position.emit({
        latLng,
        timezone: await this.mapService.getTz(latLng),
      });
    }

    this.coordinates = currentCoordinates;

    if (this.marker) {
      this.marker.setMap(null);
    }
    this.marker = await this.mapService.getMarker(this.map, latLng);
    this.map.setCenter(latLng);
    this.map.setZoom(DEFAULT_ZOOM);
    this.changeDetectorRef.detectChanges();
  }
}
